import { Measures } from "../../../telemetry/Measures";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { getHostInstanceManagerApi } from "../../externalApi/InstanceManagerApi";
import { SetStaticWidgetPinnedStatus } from "../../externalApi/WidgetPickerApi";
import { WidgetRegistration, getWidgetRegistrarAsync } from "../registrar/registrar";
import { openDB } from "idb";
import { WidgetInstance } from "./widgetInstanceInternal";
const dashboardDbPath = "com.microsoft.dashboard";
const widgetStoreName = "widgetInstances";
let g_feedInstance = undefined;
class WidgetInstanceSerialized {
    constructor(registration, widgetId, instanceId, cachedProperties, widgetDesignation) {
        this.registration = registration;
        this.instanceId = instanceId;
        this.widgetId = widgetId;
        this.cachedProperties = cachedProperties;
        this.widgetDesignation = widgetDesignation;
    }
}
class WidgetInstanceManagerImpl {
    constructor() {
        Measures.logEvent(dashboard, "WebAppWidgetInstanceManagerCreated", 16777216 /* productAndServicePerformance */);
        this.riverInstanceArray = [];
        this.widgetInstanceArray = [];
        this.widgetInstanceArrayPromise = null;
        this.feedWidgetInstancePromise = null;
        this.dbName = dashboardDbPath;
        // Construction is async due to our reliance on the indexedDB init, so we use a readiness pattern
        this.isDatabaseOpened = new Promise(async (resolve, reject) => {
            let registeredWidgets = await (await getWidgetRegistrarAsync()).getWidgetRegistrations();
            // Get the list of pinned 3p widget instances known to the host and purge the indexdb of widgets that don't exist
            let hostWidgetSet = undefined;
            let instanceManagerApi = await getHostInstanceManagerApi();
            if (instanceManagerApi) {
                let hostWidgetInstancesPromise = new Promise(async (hostResolve, hostReject) => {
                    await (instanceManagerApi === null || instanceManagerApi === void 0 ? void 0 : instanceManagerApi.getWidgetIds((hresult, widgetIds) => {
                        if (hresult == 0) {
                            hostResolve(widgetIds);
                        }
                        else {
                            Trace.logEvent(dashboard, "GetWidgetIdsFailure", 0 /* verbose */, {
                                hresult: hresult,
                            });
                            hostResolve(undefined);
                        }
                    }));
                });
                // Convert to set for fast lookup, undefined is failure to retrieve the list
                const hostWidgets = await hostWidgetInstancesPromise;
                if (hostWidgets) {
                    hostWidgetSet = new Set(hostWidgets);
                }
            }
            this.request = window.indexedDB.open(dashboardDbPath);
            this.request.onupgradeneeded = (result) => {
                Trace.logEvent(dashboard, "InstanceManagerFRE", 0 /* verbose */, {});
                this.dataMigrationFinished = new Promise(async (resolveUpgrade) => {
                    let db = this.request.result;
                    // Create the widgetInstances objectStore for our database
                    if (!db.objectStoreNames.contains(widgetStoreName)) {
                        db.createObjectStore(widgetStoreName);
                    }
                    resolveUpgrade();
                });
            };
            this.request.onerror = function (event) {
                let eventReceived = event.target !== null ? event.target : null;
                let errorMessage = eventReceived !== null && eventReceived.error !== null ? eventReceived.error.message : "";
                Measures.logEvent(dashboard, "InstanceManagerCreationFailed", 16777216 /* productAndServicePerformance */, {
                    error: errorMessage,
                });
                reject(eventReceived);
            };
            this.request.onsuccess = (result) => {
                Measures.logEvent(dashboard, "InstanceManagerCreationSuccessful", 16777216 /* productAndServicePerformance */, {});
                // If dataMigrationFinished doesn't exist, we are not in an upgrade scenario
                // which means we just need to validate that 3rd party widgets are still installed
                if (this.dataMigrationFinished === undefined) {
                    this.dataMigrationFinished = new Promise(async (resolveUpgrade) => {
                        let keysResult = await this.getSerializedWidgets([widgetStoreName]);
                        const db = this.request.result;
                        const tx = db.transaction(widgetStoreName, "readwrite");
                        const store = tx.objectStore(widgetStoreName);
                        const registeredIDs = new Set();
                        registeredWidgets.forEach((w) => {
                            registeredIDs.add(w.id);
                        });
                        await Promise.all(keysResult.map(async (storedWidget) => {
                            // If a widget's no longer exists in the registrar, delete it
                            if (storedWidget.registration.isAdaptiveCard) {
                                if (!registeredIDs.has(storedWidget.registration.id)) {
                                    Measures.logEvent(dashboard, "WidgetDeletedFromDB", 16777216 /* productAndServicePerformance */, {
                                        appId: storedWidget.registration.appId,
                                        indexedDBInstanceId: storedWidget.instanceId,
                                        providerId: storedWidget.registration.providerId,
                                        reason: "PFNUninstalled",
                                        widgetDefinitionId: storedWidget.registration.id,
                                        widgetInstanceId: storedWidget.widgetId,
                                    });
                                    store.delete([storedWidget.registration.id, storedWidget.instanceId]);
                                }
                                else if (storedWidget.registration.entryPoint.type === "PLATFORM") {
                                    // Check to ensure that the platform stil exists and if not remove it.
                                    // If hte host has no widgets assume that it failed to retrieve them.
                                    if (hostWidgetSet && !hostWidgetSet.has(storedWidget.widgetId)) {
                                        Measures.logEvent(dashboard, "WidgetDeletedFromDB", 16777216 /* productAndServicePerformance */, {
                                            appId: storedWidget.registration.appId,
                                            indexedDBInstanceId: storedWidget.instanceId,
                                            providerId: storedWidget.registration.providerId,
                                            reason: "NotInService",
                                            widgetDefinitionId: storedWidget.registration.id,
                                            widgetInstanceId: storedWidget.widgetId,
                                        });
                                        store.delete([storedWidget.widgetId, storedWidget.instanceId]);
                                    }
                                }
                            }
                        }));
                        resolveUpgrade();
                    });
                }
                // If we made it this far, assume IndexedDB has our widgets
                resolve();
            };
        });
    }
    getRiverWidgetInstances() {
        return this.riverInstanceArray;
    }
    async getWidgetInstances() {
        Trace.logEvent(dashboard, "WidgetInstanceManagerWidgetInstancesRequested", 0 /* verbose */);
        await this.getInstanceManagerReadiness();
        if (this.widgetInstanceArrayPromise === null) {
            this.widgetInstanceArrayPromise = new Promise((resolve, reject) => {
                this.getSerializedWidgets([widgetStoreName]).then((result) => {
                    this.widgetInstanceArray = result.map(this.deserializeWidget, this);
                    Measures.logEvent(dashboard, "WidgetInstanceManagerWidgetInstancesDeserialized", 16777216 /* productAndServicePerformance */);
                    // Separate trace-level event for tracking which widgets were deserialized in traces
                    Trace.logEvent(dashboard, "WidgetInstanceManagerWidgetInstancesDeserializedTraceLevel", 0 /* verbose */, { WidgetInstances: this.widgetInstanceArray });
                    resolve(this.widgetInstanceArray);
                });
            });
        }
        return this.widgetInstanceArrayPromise;
    }
    async getFeedInstance() {
        Trace.logEvent(dashboard, "WidgetInstanceManagerFeedInstanceRequested", 0 /* verbose */);
        await this.getInstanceManagerReadiness();
        if (this.feedWidgetInstancePromise === null) {
            this.feedWidgetInstancePromise = new Promise((resolve, reject) => {
                const feedEntryPoint = {
                    "className": "peregrine-widgets",
                    "configuration": {
                        "configRef": "DesktopFeedWC/default",
                        "experienceName": "DesktopFeedWC",
                        "widgetGroup": "widgetGroup-windowsNewsPlus"
                    }
                };
                const feedPlaceholders = [
                    {
                        size: "large",
                        src: "images/feed-loading-light.svg",
                        theme: "light",
                    },
                    {
                        size: "large",
                        src: "images/feed-loading-dark.svg",
                        theme: "dark",
                    }
                ];
                let feedRegistration = new WidgetRegistration("", // widgetDefinition.displayName,
                "", // widgetDefinition.providerDisplayName,
                feedEntryPoint, { "src": "" }, // icon
                "com.msn.desktopfeed", "com.msn.desktopfeed", "com.msn.desktopfeed", false, // isDefault pinned widget, TODO: http://task.ms/40365998 Need to find a new way to describe this or eliminate it.
                feedPlaceholders, "", // backingAppPFN, does not apply to feed registration
                undefined, // widget.entryPointOverrides,
                undefined, 1);
                // Instantiate the global
                let randomId = this.getRandomInstanceID();
                g_feedInstance = new WidgetInstance(feedRegistration, "com.msn.desktopfeed", // For a platform widget this will be the generated instance unique guid string
                randomId, // old instance ID should not be used by anything
                "com.msn.desktopfeed", randomId);
                resolve(g_feedInstance);
            });
        }
        return this.feedWidgetInstancePromise;
    }
    async updateWidgetInstance(widget) {
        if (this.widgetInstanceArray.find((pinnedWidget) => widget.widgetId === pinnedWidget.widgetId)) {
            return this.storeWidgetInstance(widget, false, widget.widgetDesignation);
        }
        else {
            // If we aren't dealing with a pinned instance then don't store the update, just hand back the instance
            return Promise.resolve(widget);
        }
    }
    async addWidgetInstanceForPin(registration, widgetId) {
        let riverInstance = this.riverInstanceArray.find((riverWidget) => riverWidget.widgetId == widgetId || riverWidget.widgetDefinitionId == registration.id);
        if (riverInstance) {
            // Pinning from the river
            // Remove the instance from the river instance array before adding it to the pinned instance array
            this.riverInstanceArray.splice(this.riverInstanceArray.indexOf(riverInstance), 1);
            // pin means its loaded, skip setting isLoadForAddedInstancePending
            this.widgetInstanceArray.push(riverInstance);
            // Try send static widget pinned status to host app/xaml picker
            if (!riverInstance.isPlatform) {
                SetStaticWidgetPinnedStatus(riverInstance.widgetDefinitionId, /*pinned*/ true);
            }
            //Tell the host that this is now a pinned instance
            let hostInstanceManager = await getHostInstanceManagerApi(2 /*api version 2*/);
            if (hostInstanceManager && hostInstanceManager.setWidgetPinnedAsync) {
                await hostInstanceManager.setWidgetPinnedAsync(riverInstance.widgetId, true /* pinned */);
            }
            return this.storeWidgetInstance(riverInstance, /*firstRun*/ false, "widget");
        }
        else {
            // When tombstoning peregrine widgets, it will try to create widget instances twice
            // (1) User pins widget from picker, onWidgetPicked
            // (2) WebXT calls to pin tombstoned widget, createPinPreference
            // If the widget has already been created, return existing instance.
            if (registration.id.startsWith("com.")) {
                let widgetInstance = (await this.getWidgetInstances()).find((widgetInstance) => {
                    return widgetInstance.widgetId == registration.id;
                });
                if (widgetInstance) {
                    return widgetInstance;
                }
            }
            let randomId = this.getRandomInstanceID();
            let newWidget = new WidgetInstance(registration, undefined, randomId, undefined, randomId);
            // If the widget is added for a platform definition, it needs to be instantiated.
            if (newWidget.isPlatform) {
                // Tell the widget to construct its platform instance
                await newWidget.createWidgetPlatformInstance(/*pinned*/ true);
            }
            newWidget.isLoadForAddedInstancePending = true;
            this.widgetInstanceArray.push(newWidget);
            // Try send static widget pinned status to host app/xaml picker
            if (!newWidget.isPlatform) {
                SetStaticWidgetPinnedStatus(newWidget.widgetDefinitionId, /*pinned*/ true);
            }
            return this.storeWidgetInstance(newWidget, /*firstRun*/ false, "widget");
        }
    }
    async addWidgetInstanceForRiver(registration) {
        const instanceId = this.getRandomInstanceID();
        const newWidget = new WidgetInstance(registration, undefined, instanceId, undefined, instanceId);
        // If the widget is added for a platform definition, it needs to be instantiated.
        if (newWidget.isPlatform) {
            // Tell the widget to construct its platform instance
            // This create may return a previous not pinned instance.  There should only ever be one.
            await newWidget.createWidgetPlatformInstance(/*pinned*/ false);
        }
        this.riverInstanceArray.push(newWidget);
        return newWidget;
    }
    async removeWidgetInstanceFromPins(widgetInstance) {
        return new Promise(async (resolve) => {
            this.widgetInstanceArray.splice(this.widgetInstanceArray.indexOf(widgetInstance), 1);
            const db = await openDB(this.dbName);
            const tx = db.transaction(widgetStoreName, "readwrite");
            const store = tx.store;
            // WebXT doesn't always send correct instance ids for pin/unpin events causing our pinned list to become inaccurate
            // Since we don't support multi-instance currently, get all matching widget ids and remove.
            const storedWidgets = await (await store.getAllKeys()).filter((key) => key[0] === widgetInstance.widgetId);
            if (storedWidgets === undefined || storedWidgets.length == 0) {
                Measures.logEvent(dashboard, "RemoveWidgetFailed", 33554432 /* productAndServiceUsage */, {
                    appId: widgetInstance.widgetRegistration.appId,
                    indexedDBInstanceId: widgetInstance.instanceId,
                    providerId: widgetInstance.widgetRegistration.providerId,
                    reason: "WidgetNotFound",
                    widgetDefinitionId: widgetInstance.widgetRegistration.id,
                    widgetInstanceId: widgetInstance.widgetId,
                });
            }
            else {
                for (const key of storedWidgets) {
                    await store.delete(key);
                }
                // Not firing telemetry event for each instance being removed as that can be too noisy.
                Measures.logEvent(dashboard, "RemoveWidgetSucceeded", 33554432 /* productAndServiceUsage */, {
                    appId: widgetInstance.widgetRegistration.appId,
                    indexedDBInstanceId: widgetInstance.instanceId,
                    numInstancesRemoved: storedWidgets.length,
                    providerId: widgetInstance.widgetRegistration.providerId,
                    widgetDefinitionId: widgetInstance.widgetRegistration.id,
                    widgetInstanceId: widgetInstance.widgetId,
                });
                if (widgetInstance.isPlatform) {
                    // Tell the host and platform to forget this widget and delete any cached data
                    let hostInstanceManager = await getHostInstanceManagerApi();
                    if (hostInstanceManager) {
                        await hostInstanceManager.removeWidget(widgetInstance.widgetId);
                    }
                }
                // Try send static widget un-pinned status to host app/xaml picker
                if (!widgetInstance.isPlatform) {
                    SetStaticWidgetPinnedStatus(widgetInstance.widgetId, /*unpinned*/ false);
                }
            }
            resolve(widgetInstance);
        });
    }
    isRiverInstance(widgetInstance) {
        return undefined !== this.riverInstanceArray.find((riverWidget) => riverWidget.widgetId == widgetInstance.widgetId);
    }
    async removeWidgetInstanceFromRiver(widgetInstance) {
        let removedInstances = this.riverInstanceArray.splice(this.widgetInstanceArray.indexOf(widgetInstance), 1);
        if (removedInstances.length > 0) {
            if (widgetInstance.isPlatform) {
                // Tell the host and platform to forget this widget and delete any cached data
                let hostInstanceManager = await getHostInstanceManagerApi();
                if (hostInstanceManager) {
                    await hostInstanceManager.removeWidget(widgetInstance.widgetId);
                }
            }
            // Try send static widget un-pinned status to host app/xaml picker
            if (!widgetInstance.isPlatform) {
                SetStaticWidgetPinnedStatus(widgetInstance.widgetId, /*unpinned*/ false);
            }
            return widgetInstance;
        }
        else {
            throw new Error("Widget " + widgetInstance.widgetId + " is not in the river");
        }
    }
    async RegisterPlatformEvents() {
        let hostInstanceManagerApi = await getHostInstanceManagerApi();
        if (hostInstanceManagerApi) {
            hostInstanceManagerApi.addEventListener("widgetModified", this.onPlatformWidgetUpdated.bind(this));
        }
    }
    async getSerializedWidgets(objectStores) {
        if (objectStores === undefined) {
            objectStores = [widgetStoreName];
        }
        let widgets = [];
        const db = await openDB(dashboardDbPath);
        // Create event handlers for both success and failure
        db.onerror = function (event) {
            let eventReceived = event.target !== null ? event.target : null;
            let errorMessage = eventReceived !== null && eventReceived.error !== null ? eventReceived.error.message : "";
            Measures.logEvent(dashboard, "InstanceManagerDBOpenFailed", 16777216 /* productAndServicePerformance */, { context: "GetSerializedWidgets", error: errorMessage });
        };
        for (const storeName of objectStores) {
            let transaction = db.transaction(storeName, "readonly");
            transaction.onerror = () => {
                return new Error("widgetInstances store not found");
            };
            let store = transaction.objectStore(storeName);
            const storeWidgets = await store.getAll();
            widgets = widgets.concat(storeWidgets);
        }
        db.close();
        return Promise.resolve(widgets);
    }
    async storeWidgetInstance(widgetInstance, isFirstRun, widgetDesignation) {
        if (!isFirstRun) {
            await this.getInstanceManagerReadiness();
        }
        return new Promise((resolve, reject) => {
            const DBOpenRequest = window.indexedDB.open(dashboardDbPath);
            // Create event handlers for both success and failure
            DBOpenRequest.onerror = function (event) {
                let eventReceived = event.target !== null ? event.target : null;
                let errorMessage = eventReceived !== null && eventReceived.error !== null ? eventReceived.error.message : "";
                Measures.logEvent(dashboard, "InstanceManagerDBOpenFailed", 16777216 /* productAndServicePerformance */, { context: "storeWidgetInstance", error: errorMessage });
                reject(new Error("DbNotFound"));
            };
            DBOpenRequest.onsuccess = () => {
                let db = DBOpenRequest.result;
                // Open a transaction to the database
                let transaction = db.transaction([widgetStoreName], "readwrite");
                // Report on the success of the transaction completing, when everything is done
                // Hits after objectStoreRequest completion
                transaction.oncomplete = () => {
                    let cachedProperties = widgetInstance.getAllWidgetProperties();
                    Measures.logEvent(dashboard, "StoreWidgetInstanceSucceeded", 16777216 /* productAndServicePerformance */, {
                        appId: widgetInstance.widgetRegistration.appId,
                        indexedDBInstanceId: widgetInstance.instanceId,
                        isTombstoned: widgetInstance.widgetRegistration.isTombstoned,
                        providerId: widgetInstance.widgetRegistration.providerId,
                        widgetDefinitionId: widgetInstance.widgetRegistration.id,
                        widgetInstanceId: widgetInstance.widgetId,
                        widgetProperties: cachedProperties,
                    });
                    resolve(widgetInstance);
                };
                transaction.onerror = function (event) {
                    let eventReceived = event.target !== null ? event.target : null;
                    let errorMessage = eventReceived !== null && eventReceived.error !== null ? eventReceived.error.message : "";
                    Measures.logEvent(dashboard, "StoreWidgetInstanceFailed", 16777216 /* productAndServicePerformance */, {
                        appId: widgetInstance.widgetRegistration.appId,
                        error: errorMessage,
                        indexedDBInstanceId: widgetInstance.instanceId,
                        providerId: widgetInstance.widgetRegistration.providerId,
                        widgetDefinitionId: widgetInstance.widgetRegistration.id,
                        widgetInstanceId: widgetInstance.widgetId,
                    });
                    reject(new Error("Transaction not opened due to error."));
                };
                let store = transaction.objectStore(widgetStoreName);
                // Updates an entry if it already exists, otherwise creates a new one
                let objectStoreRequest = store.put(this.serializeWidget(widgetInstance), [
                    widgetInstance.widgetId,
                    widgetInstance.instanceId,
                ]);
                objectStoreRequest.onsuccess = () => {
                    // report the success of our request
                    // (to detect whether it has been successfully
                    // added to the database, you'd look at transaction.oncomplete)
                };
                objectStoreRequest.onerror = function (event) {
                    reject(new Error("WidgetNotFound"));
                };
                db.close();
            };
        });
    }
    serializeWidget(widget) {
        return new WidgetInstanceSerialized(widget.widgetRegistration, widget.widgetId, // For a platform widget this will be the generated instance unique guid string
        widget.instanceId, widget.cachedProperties, widget.widgetDesignation);
    }
    deserializeWidget(serializedWidget) {
        // Force all non widget platform widgets to be single instance
        if (!serializedWidget.registration.entryPoint.type || serializedWidget.registration.entryPoint.type !== "PLATFORM") {
            serializedWidget.registration.maxInstances = 1;
        }
        return new WidgetInstance(serializedWidget.registration, serializedWidget.widgetId, // For a platform widget this will be the generated instance unique guid string
        serializedWidget.instanceId, serializedWidget.widgetId, serializedWidget.instanceId, serializedWidget.widgetDesignation, serializedWidget.cachedProperties);
    }
    getRandomInstanceID() {
        let rand = null;
        while (rand === null) {
            rand = Math.round(Math.random() * (Number.MAX_SAFE_INTEGER - 1));
        }
        return rand;
    }
    async getInstanceManagerReadiness() {
        await this.isDatabaseOpened;
        if (this.dataMigrationFinished) {
            await this.dataMigrationFinished;
        }
    }
    async onPlatformWidgetUpdated(widgetIdUpdated) {
        // Find the widget instance and cause it to re-render
        Measures.logEvent(dashboard, "onPlatformWidgetUpdate", 16777216 /* productAndServicePerformance */, {
            widgetInstanceId: widgetIdUpdated,
        });
        let platformWidget = this.widgetInstanceArray.find((widget) => widget.widgetId === widgetIdUpdated);
        if (!platformWidget) {
            // If not a pinned instance check the river instances
            platformWidget = this.getRiverWidgetInstances().find((widget) => widget.widgetId === widgetIdUpdated);
        }
        if (platformWidget) {
            let lifecycleApi = platformWidget.tryGetApi("lifecycle", 1, 1);
            if (lifecycleApi) {
                await lifecycleApi.onRefresh();
            }
            // If any payload comes in from the provider, reset the context menu to be enabled.
            // "hidesettings" here means to hide the customization UI which will re-enable the menu button.
            const settingsApi = platformWidget.tryGetApi("settings", 1, 1);
            if (settingsApi) {
                settingsApi.hideSettings();
            }
        }
        else {
            Measures.logEvent(dashboard, "onPlatformWidgetUpdateFailed", 16777216 /* productAndServicePerformance */, {
                widgetInstanceId: widgetIdUpdated,
            });
        }
    }
}
// Singleton instance
let g_widgetInstanceManagerInstance;
export async function getWidgetInstanceManager() {
    if (!g_widgetInstanceManagerInstance) {
        g_widgetInstanceManagerInstance = new WidgetInstanceManagerImpl();
        await g_widgetInstanceManagerInstance.isDatabaseOpened.catch((reason) => {
            // We have a one-time fallback in place for when a VersionError occurs
            if ((reason === null || reason === void 0 ? void 0 : reason.error) && reason.error.name === "VersionError") {
                // In a VersionError, we should get a message of "The requested version (x) is less than the existing version (y)."
                // Grab the numbers via regex and fallback to existing version y in these cases
                let regex = /\d+/g;
                let match = reason.error.message.match(regex);
                if (match && match.length === 2) {
                    g_widgetInstanceManagerInstance = new WidgetInstanceManagerImpl();
                    Measures.logEvent(dashboard, "WidgetInstanceManagerVersionErrorFallback", 16777216 /* productAndServicePerformance */);
                }
            }
        });
        await g_widgetInstanceManagerInstance.RegisterPlatformEvents();
    }
    return g_widgetInstanceManagerInstance;
}
